/**
 * NoteCancelTab.jsx — Muhasebe FAZ 4.8, "Senet İptal" iç sekmesi.
 *
 * Uç: POST /admin/promissory-notes/{note_id}/cancel  Body: { reason }
 *
 * İki şeyi kopyada NET söylemek zorundayız (yanlış anlaşılırsa admin borcu
 * sildiğini sanır):
 *   1. İptal SİLME DEĞİLDİR — borç silinmez, bağlı TAKSİT AÇIK KALIR.
 *   2. Senedin taksite verdiği FAİZ MUAFİYETİ KALKAR; taksit normal gecikme
 *      faizine tabi olur (backend `interest_exemption_lifted: true`).
 *
 * `reason` boş/whitespace ise istek HİÇ GÖNDERİLMEZ (backend 400
 * `senet_iptal_gerekce_zorunlu` döner; kullanıcıya hata yedirmiyoruz).
 */
import { useEffect, useMemo, useState } from 'react';
import { Ban } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirmDialog } from '../../../components/admin';
import { useCancelPromissoryNote } from '../../../api/queries/accounting';
import {
  ActionButton,
  Field,
  INPUT_CLASS,
  InlineAlert,
  NotePicker,
  TabIntro,
  noteErrorMessage,
} from './noteUiKit';
import { isActiveNote } from './promissoryMath';

export default function NoteCancelTab({ userId, notes, initialNoteId }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const cancelNote = useCancelPromissoryNote(userId);

  const [noteId, setNoteId] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  const activeNotes = useMemo(() => (notes || []).filter((n) => isActiveNote(n.status)), [notes]);

  // "Senet Düzenleme" sekmesindeki "İptal Et" butonu bu sekmeye yönlendirip
  // senedi ön-seçer (aynı senet için iki kere arama yapılmasın).
  useEffect(() => {
    if (initialNoteId) setNoteId(initialNoteId);
  }, [initialNoteId]);

  const noteMissing = !noteId;
  const reasonMissing = reason.trim().length === 0;
  const canSubmit = !noteMissing && !reasonMissing;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;

    const ok = await confirm({
      title: t('accounting.notes.cancel.confirm_title'),
      description: `${t('accounting.notes.cancel.confirm_description')} ${t('accounting.notes.irreversible')}`,
      variant: 'danger',
      confirmLabel: t('accounting.notes.confirm_continue'),
    });
    if (!ok) return;

    try {
      const res = await cancelNote.mutateAsync({ noteId, reason: reason.trim() });
      setResult(res || {});
      toast.success(t('accounting.notes.cancel.success'));
      setNoteId('');
      setReason('');
      setSubmitted(false);
    } catch (err) {
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <TabIntro title={t('accounting.notes.cancel.title')} description={t('accounting.notes.cancel.description')} />

      <InlineAlert variant="warning" title={t('accounting.notes.cancel.effect_title')} testId="note-cancel-effect">
        {t('accounting.notes.cancel.effect_desc')}
      </InlineAlert>

      {result && (
        <InlineAlert
          variant="info"
          title={t('accounting.notes.cancel.result_title')}
          testId="note-cancel-result"
        >
          {result.interest_exemption_lifted
            ? t('accounting.notes.cancel.result_exemption_lifted')
            : t('accounting.notes.cancel.result_no_installment')}
        </InlineAlert>
      )}

      <NotePicker
        id="note-cancel-note"
        testId="note-cancel-select"
        notes={activeNotes}
        value={noteId}
        onChange={(v) => setNoteId(v)}
        label={t('accounting.notes.cancel.note_label')}
        required
        placeholder={t('accounting.notes.select_placeholder')}
        emptyText={t('accounting.notes.cancel.no_active_notes')}
        error={submitted && noteMissing ? t('accounting.notes.note_required') : ''}
        lang={lang}
        t={t}
      />

      <Field
        id="note-cancel-reason"
        label={t('accounting.notes.cancel.reason_label')}
        required
        hint={t('accounting.notes.cancel.reason_hint')}
        error={submitted && reasonMissing ? t('accounting.notes.cancel.reason_required') : ''}
      >
        {({ id, describedBy }) => (
          <textarea
            id={id}
            rows={3}
            className={INPUT_CLASS}
            value={reason}
            placeholder={t('accounting.notes.cancel.reason_placeholder')}
            aria-required
            aria-invalid={submitted && reasonMissing ? true : undefined}
            aria-describedby={describedBy}
            onChange={(e) => setReason(e.target.value)}
            data-testid="note-cancel-reason-input"
          />
        )}
      </Field>

      <div className="flex justify-end">
        <ActionButton
          type="submit"
          variant="danger"
          icon={Ban}
          loading={cancelNote.isPending}
          disabled={cancelNote.isPending}
          testId="note-cancel-submit"
        >
          {cancelNote.isPending ? t('accounting.notes.submitting') : t('accounting.notes.cancel.submit')}
        </ActionButton>
      </div>
    </form>
  );
}
